import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with credentials for cookie-based auth
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // CRITICAL: Send cookies with cross-origin requests
  timeout: 60000, // 60 second timeout for AI operations
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('API Request - Token attached:', token.substring(0, 20) + '...');
    } else {
      console.log('API Request - No token in localStorage');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only clear auth token on 401 from auth verification endpoint
    // Don't clear on other 401s (like permissions errors)
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Only clear token if it's an auth check endpoint that failed
      if (url.includes('/auth/me')) {
        localStorage.removeItem('auth_token');
      }
    }
    return Promise.reject(error);
  }
);

// Add rate limit handling
import setupInterceptors from './rateLimitInterceptor';
setupInterceptors(api);
// Auth API
export const authAPI = {
  // Check if custom Google OAuth is enabled
  getGoogleAuthConfig: async () => {
    const response = await api.get('/auth/google/config');
    return response.data;
  },

  // Google OAuth - AlignifyNOW Custom Flow (Reads from backend API - SECURE)
  // Supports promo codes for new user registration bonus credits
  initiateGoogleLogin: async (redirectPath = '/auth/callback', promoCode = null) => {
    try {
      console.log('[AUTH] Starting Google login flow...');
      
      // Store promo code in localStorage to be read during callback
      if (promoCode) {
        localStorage.setItem('promo_code', promoCode);
        console.log('[AUTH] Promo code stored:', promoCode);
      }
      
      // Get OAuth config from backend (credentials stored securely on server)
      const config = await authAPI.getGoogleAuthConfig();
      console.log('[AUTH] Config:', config);
      
      if (config.custom_oauth_enabled) {
        // Get auth URL from backend
        const redirectUri = window.location.origin + redirectPath;
        const response = await api.get('/auth/google/url', { 
          params: { redirect_uri: redirectUri } 
        });
        console.log('[AUTH] Redirecting to Google...');
        window.location.href = response.data.auth_url;
      } else {
        // Fallback to Emergent Auth only if custom not configured
        console.log('[AUTH] Custom OAuth not enabled, using Emergent');
        const redirectUrl = window.location.origin + redirectPath;
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      }
    } catch (error) {
      console.error('[AUTH] Error:', error);
      // Fallback to Emergent Auth on error
      const redirectUrl = window.location.origin + redirectPath;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  },

  // Exchange Google authorization code (custom flow)
  exchangeGoogleCode: async (code, redirectUri, promoCode = null) => {
    const payload = { code, redirect_uri: redirectUri };
    if (promoCode) {
      payload.promo_code = promoCode;
    }
    const response = await api.post('/auth/google/callback', payload);
    return response.data;
  },

  // Exchange Emergent session_id (legacy/fallback flow)
  exchangeGoogleSession: async (sessionId) => {
    const response = await api.post('/auth/google/session', { session_id: sessionId }, {
      withCredentials: true
    });
    return response.data;
  },

  getGoogleUser: async () => {
    // Token is automatically added by the request interceptor
    const response = await api.get('/auth/me/google');
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  // Legacy Magic Link (kept for backwards compatibility)
  requestMagicLink: async (email) => {
    const response = await api.post('/auth/request-magic-link', { email });
    return response.data;
  },

  verifyMagicLink: async (token) => {
    const response = await api.post('/auth/verify-magic-link', { token });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  }
};

// Payment API
export const paymentAPI = {
  getPackages: async () => {
    const response = await api.get('/packages');
    return response.data;
  },

  createCheckoutSession: async (analysisId, packageType, originUrl) => {
    const response = await api.post('/checkout/session', {
      analysis_id: analysisId,
      package_type: packageType,
      origin_url: originUrl
    });
    return response.data;
  },

  getCheckoutStatus: async (sessionId) => {
    const response = await api.get(`/checkout/status/${sessionId}`);
    return response.data;
  },
  verifyMissingPayments: async () => {
    const response = await api.post('/payments/verify-missing');
    return response.data;
  },
};

// Analysis API
export const analysisAPI = {
  createAnalysis: async (formData) => {
    const response = await api.post('/analyses', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  triggerProcessing: async (analysisId) => {
    const response = await api.post(`/analyses/${analysisId}/process`);
    return response.data;
  },

  getAnalyses: async () => {
    const response = await api.get('/analyses');
    return response.data;
  },

  getAnalysis: async (id) => {
    const response = await api.get(`/analyses/${id}`);
    return response.data;
  },

  deleteAnalysis: async (id) => {
    const response = await api.delete(`/analyses/${id}`);
    return response.data;
  },

  downloadReport: async (id) => {
    const response = await api.get(`/analyses/${id}/download/report`, {
      responseType: 'blob',
    });
    return response.data;
  },

  downloadSuggestions: async (id) => {
    const response = await api.get(`/analyses/${id}/download/suggestions`, {
      responseType: 'blob',
    });
    return response.data;
  },

  downloadCoverLetter: async (id) => {
    const response = await api.get(`/analyses/${id}/download/cover-letter`, {
      responseType: 'blob',
    });
    return response.data;
  }
};

// Admin API
export const adminAPI = {
  getAnalyses: async (skip = 0, limit = 100) => {
    const response = await api.get(`/admin/analyses?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  getAnalysisDetails: async (analysisId) => {
    const response = await api.get(`/admin/analyses/${analysisId}`);
    return response.data;
  },

  rerunAnalysis: async (analysisId, notes = null) => {
    const response = await api.post(`/admin/analyses/${analysisId}/rerun`, {
      notes
    });
    return response.data;
  },

  deleteAnalysis: async (analysisId) => {
    const response = await api.delete(`/admin/analyses/${analysisId}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  getSubscriberStats: async () => {
    const response = await api.get('/admin/subscribers');
    return response.data;
  },

  promoteUser: async (email) => {
    const formData = new FormData();
    formData.append('user_email', email);
    const response = await api.post('/admin/promote', formData);
    return response.data;
  }
};

// Free Tools API - Updated to Team Specifications
export const freeToolsAPI = {
  // Get user credits info
  getCredits: async () => {
    const response = await api.get('/user/credits', {
      withCredentials: true
    });
    return response.data;
  },

  // ========================================
  // CLEAN SIMPLE MATCH API (Refactored)
  // ========================================
  simpleMatch: async (jobDescription, resumeText) => {
    console.log('[API] simpleMatch called');
    console.log('[API] Job desc length:', jobDescription?.length);
    console.log('[API] Resume length:', resumeText?.length);
    
    const response = await api.post('/match/simple', {
      job_description: jobDescription,
      resume_text: resumeText
    }, {
      timeout: 60000  // 60 second timeout
    });
    
    console.log('[API] simpleMatch raw response:', response.data);
    
    // Return exactly what backend sends
    return {
      match_score: response.data.match_score,
      missing_skills: response.data.missing_skills,
      summary: response.data.summary
    };
  },

  // New Match Lite API with team's specifications (keeping for landing page)
  matchLiteV2: async (jobDescription, resumeText = null, resumeFile = null) => {
    // If just text (no file), use the simpler endpoint
    if (!resumeFile && resumeText) {
      return freeToolsAPI.simpleMatch(jobDescription, resumeText);
    }
    
    const formData = new FormData();
    formData.append('job_description', jobDescription);
    
    if (resumeFile) {
      formData.append('resume_file', resumeFile);
    } else if (resumeText) {
      formData.append('resume_text', resumeText);
    }
    
    // Use axios with the configured baseURL for proper CORS handling
    // Extended timeout (60s) for AI processing of long descriptions
    const response = await api.post('/v1/free-tools/match-lite', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,  // Include cookies for auth
      timeout: 60000  // 60 second timeout for AI operations
    });
    
    return response.data;
  },

  // Legacy Match Lite API (keep for backward compatibility)
  matchLite: async (resumeText, jobUrlOrText, clientMeta = { source: "web" }) => {
    const response = await api.post('/match-lite', {
      resume_text: resumeText,
      job_url_or_text: jobUrlOrText,
      client_meta: clientMeta
    });
    return response.data;
  },

  trackAnalyticsEvent: async (event, properties = {}) => {
    const response = await api.post('/analytics/track', {
      event,
      properties
    });
    return response.data;
  },

  // Cover Letter Starter API
  coverLetterStarter: async (jobDescription, resumeText = null, resumeFile = null, companyContext = null) => {
    const formData = new FormData();
    formData.append('job_description', jobDescription);
    
    if (resumeFile) {
      formData.append('resume_file', resumeFile);
    } else if (resumeText) {
      formData.append('resume_text', resumeText);
    }
    
    if (companyContext) {
      formData.append('company_context', companyContext);
    }
    
    // Use axios with the configured baseURL for proper CORS handling
    const response = await api.post('/v1/free-tools/cover-letter-starter', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true  // Include cookies for auth
    });
    
    return response.data;
  }
};

// Job Tracker API
export const jobTrackerAPI = {
  getLimits: async () => {
    const response = await api.get('/jobapps/limits');
    return response.data;
  },

  createApplication: async (applicationData) => {
    const response = await api.post('/jobapps', applicationData);
    return response.data;
  },

  getApplications: async (status = null, limit = 100, offset = 0) => {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString()
    });
    if (status) {
      params.append('status', status);
    }
    
    const response = await api.get(`/jobapps?${params}`);
    return response.data;
  },

  updateApplication: async (applicationId, updateData) => {
    const response = await api.patch(`/jobapps/${applicationId}`, updateData);
    return response.data;
  },

  deleteApplication: async (applicationId) => {
    const response = await api.delete(`/jobapps/${applicationId}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/jobapps/stats');
    return response.data;
  },

  exportCSV: async () => {
    const response = await api.get('/jobapps/export', {
      responseType: 'blob'
    });
    return response.data;
  },

  createReminder: async (jobId, reminderDate) => {
    const response = await api.post('/reminders', {
      job_id: jobId,
      reminder_date: reminderDate
    });
    return response.data;
  },

  // Future-Proof Audit
  runFutureProofAudit: async (resumeText) => {
    const response = await api.post('/future-proof/audit', { resume_text: resumeText });
    return response.data;
  }
};

// Mock Interview API
export const interviewAPI = {
  startInterview: async (jobDescription, numQuestions = 5) => {
    const response = await api.post('/interview/start', {
      job_description: jobDescription,
      num_questions: numQuestions
    });
    return response.data;
  },

  submitAnswer: async (sessionId, questionIndex, answerText) => {
    const response = await api.post(`/interview/${sessionId}/answer`, {
      question_index: questionIndex,
      answer_text: answerText
    });
    return response.data;
  },

  transcribeAudio: async (sessionId, audioBlob) => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    
    const response = await api.post(`/interview/${sessionId}/transcribe`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  completeInterview: async (sessionId) => {
    const response = await api.post(`/interview/${sessionId}/complete`);
    return response.data;
  },

  getSession: async (sessionId) => {
    const response = await api.get(`/interview/${sessionId}`);
    return response.data;
  },

  getHistory: async () => {
    const response = await api.get('/interviews/history');
    return response.data;
  }
};

// Blog API (PUBLIC - No auth required)
export const blogAPI = {
  getAllPosts: async () => {
    const response = await api.get('/blog/posts');
    return response.data;
  },

  getPost: async (slug) => {
    const response = await api.get(`/blog/posts/${slug}`);
    return response.data;
  },

  getOgTags: async (slug) => {
    const response = await api.get(`/blog/og/${slug}`);
    return response.data;
  }
};

// Checklist / Lead Magnet API (PUBLIC - No auth required)
export const checklistAPI = {
  subscribe: async (email, name = '') => {
    const response = await api.post('/checklist/subscribe', { email, name });
    return response.data;
  },

  getDownloadUrl: () => {
    return `${BACKEND_URL}/api/checklist/download`;
  }
};

// OAuth API
export const oauthAPI = {
  initOAuth: async (provider, redirectUri) => {
    const response = await api.post(`/oauth/${provider}/init`, {
      provider,
      redirect_uri: redirectUri
    });
    return response.data;
  },

  handleCallback: async (provider, code, state) => {
    const response = await api.post(`/oauth/${provider}/callback`, {
      provider,
      code,
      state
    });
    return response.data;
  },

  getStatus: async () => {
    const response = await api.get('/oauth/status');
    return response.data;
  },

  revokeAccess: async (provider) => {
    const response = await api.delete(`/oauth/${provider}`);
    return response.data;
  }
};

// Utility functions
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Utility to get URL parameters
export const getUrlParameter = (name) => {
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  const results = regex.exec(location.search);
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

export default api;